import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Image URLs
const IMAGES = {
  hero: '/hero-value-creation.jpeg',
  craftsmanCompass: 'https://customer-assets.emergentagent.com/job_69649056-df98-4ca6-bbd3-c5d20803d8d9/artifacts/ehkuvbsr_image00007.jpeg',
  mechanicOverview: 'https://customer-assets.emergentagent.com/job_4992c407-209d-4ffe-8201-36194c9609e6/artifacts/pfowt3uq_VetterTec%20Workshop_Mechanic%20at%20Screw%20Press.jpg',
};

export const ValueCreation = () => {
  const { language } = useLanguage();
  const bannerRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    if (imageRef.current) {
      gsap.to(imageRef.current, {
        yPercent: 20,
        ease: 'none',
        scrollTrigger: {
          trigger: bannerRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1
        }
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const ctaButtons = [
    { label: language === 'fr' ? 'INVESTIR AVEC NOUS' : 'INVEST WITH US', link: '/contact?type=invest' },
    { label: language === 'fr' ? 'TRAVAILLER AVEC NOUS' : 'WORK WITH US', link: '/contact?type=work' },
    { label: language === 'fr' ? 'DEVENIR PARTENAIRE' : 'PARTNER WITH US', link: '/contact?type=partner' }
  ];

  return (
    <div className="min-h-screen bg-technical-white">
      {/* Floating CTA Buttons */}
      <div className="fixed right-8 top-1/3 z-40 flex flex-col gap-3 hidden lg:flex">
        {ctaButtons.map((btn, idx) => (
          <Link
            key={idx}
            to={btn.link}
            className="text-white text-xs font-bold tracking-wider px-5 py-3 bg-light-blue hover:bg-abyssal-blue transition-all duration-300 shadow-lg whitespace-nowrap"
            style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", letterSpacing: '1px', fontSize: '10px' }}
          >
            {btn.label}
          </Link>
        ))}
      </div>

      {/* Hero Banner */}
      <section 
        ref={bannerRef}
        className="relative h-[500px] flex flex-col overflow-hidden"
        style={{ backgroundColor: '#0a1f35' }}
      >
        <div 
          ref={imageRef}
          className="absolute inset-0 bg-cover bg-center scale-110"
          style={{ backgroundImage: `url(${IMAGES.hero})` }}
        />
        <div className="absolute inset-0 bg-abyssal-blue/50" />
        
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4">
          <h1 
            className="text-4xl lg:text-5xl text-white mb-8 text-center animate-fade-in-slow" 
            style={{ 
              fontFamily: "'Bank Gothic', 'Impact', sans-serif", 
              fontWeight: 900,
              letterSpacing: '0.03em',
              textTransform: 'uppercase'
            }}
          >
            {language === 'fr' ? 'CRÉATION DE VALEUR' : 'VALUE CREATION SUPPORT'}
          </h1>
        </div>
      </section>

      {/* Block 1: Value Creation Platform Intro */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 
            className="text-2xl lg:text-3xl text-abyssal-blue text-center mb-12 lg:mb-16 max-w-5xl mx-auto"
            style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.02em' }}
          >
            {language === 'fr'
              ? "Plateforme de création de valeur fournissant un soutien aux entreprises industrielles — dans l'écosystème Ocean Peak ou en dehors."
              : "Value Creation platform providing support to industrial companies — in the Ocean Peak ecosystem or outside."}
          </h2>
          
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Text LEFT */}
            <div>
              <p 
                className="text-lg lg:text-xl text-steel-grey leading-relaxed mb-8"
                style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", lineHeight: 1.8 }}
              >
                {language === 'fr'
                  ? "Ocean Peak Management est la plateforme de création de valeur au sein d'OPK. OPM intervient à la fois dans le portefeuille Ocean Peak et auprès d'entreprises industrielles et de Private Equity pour soutenir la création de valeur dans l'écosystème industriel européen."
                  : "Ocean Peak Management is the value creation platform within OPK. OPM intervenes both across the Ocean Peak portfolio and with Industrial and Private Equity firms to support value creation across the European Industrial ecosystem."}
              </p>
              <Link 
                to="/contact?type=work"
                className="inline-flex items-center gap-2 px-6 py-3 text-white transition-colors hover:opacity-90"
                style={{ 
                  fontFamily: "'Eurostile', 'Arial', sans-serif", 
                  fontWeight: 500, 
                  fontSize: '14px', 
                  textTransform: 'uppercase', 
                  letterSpacing: '0.1em',
                  backgroundColor: '#4fb785'
                }}
              >
                {language === 'fr' ? 'Travailler avec nous' : 'Work with us'}
                <ArrowRight size={16} />
              </Link>
            </div>
            
            {/* Image RIGHT */}
            <div>
              <div className="aspect-[4/3] overflow-hidden shadow-lg relative group">
                <img 
                  src={IMAGES.craftsmanCompass}
                  alt="Talent management"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-abyssal-blue/10 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Block 2: Talent Management */}
      <section className="py-20 lg:py-28 bg-technical-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image LEFT */}
            <div>
              <div className="aspect-[4/3] overflow-hidden shadow-lg relative group">
                <img 
                  src={IMAGES.mechanicOverview}
                  alt="Factory transformation"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-abyssal-blue/10 pointer-events-none" />
              </div>
            </div>
            
            {/* Text RIGHT */}
            <div>
              <p 
                className="text-lg text-steel-grey leading-relaxed mb-6"
                style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", lineHeight: 1.8 }}
              >
                {language === 'fr'
                  ? "Nous fournissons des capacités de gestion des talents à travers le développement des personnes, le recrutement et les rôles de direction intérimaire. Nous restons en contact avec un certain nombre de managers éprouvés ayant un historique de transformation."
                  : "We provide talent management capabilities across both people development, recruiting and interim executive roles. We keep in touch with a number of proven managers with a track record of transformation."}
              </p>
              <p 
                className="text-lg text-steel-grey leading-relaxed"
                style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", lineHeight: 1.8 }}
              >
                {language === 'fr'
                  ? "Nous soutenons les entreprises dans la conception et l'exécution de leur transformation — à travers les dimensions opérationnelles, financières et stratégiques."
                  : "We support companies as they design and execute their transformation — across operational, financial and strategic dimensions."}
              </p>
            </div>
          </div>
          
          {/* Meet Our Team Button */}
          <div className="text-center mt-12 lg:mt-16">
            <Link 
              to="/team"
              className="inline-flex items-center gap-2 px-8 py-4 bg-light-blue text-white hover:bg-abyssal-blue transition-colors"
              style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", fontWeight: 500, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.1em' }}
            >
              {language === 'fr' ? 'Rencontrer notre équipe' : 'Meet our Team'}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom Spacer */}
      <div className="h-24"></div>
    </div>
  );
};
